'use client'

import { cx } from '@7f-ui'
import { Button } from '@7f-ui/components/button/button'
import { shopAsset } from '@utils/utils-config'
import Image from 'next/image'
import Link from 'next/link'
import { useTransition } from 'react'

type AppErrorPageProps = {
  chromeOffset?: boolean
  onRetry?: () => void
  variant: 'not-found' | 'server-error'
}

export function AppErrorPage({ chromeOffset = true, onRetry, variant }: AppErrorPageProps) {
  const [isPending, startTransition] = useTransition()
  const isNotFound = variant === 'not-found'

  const handleRetry = () => {
    if (!onRetry) {
      return
    }

    startTransition(() => onRetry())
  }

  return (
    <section
      className={cx(
        'relative isolate overflow-hidden',
        chromeOffset ? 'min-h-[calc(100svh-var(--full-header-height))]' : 'min-h-screen'
      )}
    >
      <Image
        src={shopAsset('/images/error-sheep.png')}
        alt=""
        fill
        priority
        className="object-cover object-right"
        sizes="100vw"
      />

      <div
        className="absolute inset-0 z-10"
        style={{
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 36.95%, rgba(0, 0, 0, 0) 63.19%)',
        }}
      >
        <div className="fluid-grid fluid-container h-full">
          <div className="col-span-12 flex min-h-full flex-col items-center justify-center py-10 text-white sm:col-start-4 sm:col-end-10 md:col-start-3 md:col-end-9 md:items-start md:py-16 xlg:py-24">
            <h1 className="mb-3 flex flex-col items-center space-y-2 sm:mb-4 md:items-start">
              {isNotFound ? <span className="font-heading5">Fehler 404</span> : null}
              <span className="text-center font-heading1 md:text-start">
                {isNotFound ? 'Seite nicht gefunden' : 'Hallo? Ist da jemand?'}
              </span>
            </h1>

            <p className="mb-8 max-w-[40rem] text-center sm:mb-7 md:text-start">
              {isNotFound
                ? 'Orte, an denen man nie gewesen ist. Du hast so einen gefunden. Als Entschuldigung für diese Sackgasse haben wir den Hamburger Künstler Dieter Braun um ein Schaf gebeten. Magst du es?'
                : 'Das konnte nur eins bedeuten: Es war niemand mehr da. Nichts. Kein Anschluss unter … doch was war das? Mit der Taschenlampe entdeckte er einen Zahlencode: 500. Und jetzt war es klar: Der Marktplatz war abgerauscht. Und während im Hintergrund die Drähte heiß liefen, Entwickler und IT laut um die Wette riefen – das Content Team zum 500. Mal auf „Reload“ klickte, beschloss er, einfach später nochmal wiederzukommen.'}
            </p>

            <div className="flex w-full max-w-md flex-col gap-3 sm:w-auto sm:flex-row">
              {onRetry ? (
                <Button
                  onClick={handleRetry}
                  disabled={isPending}
                  width="responsive"
                  variant="primary"
                >
                  Erneut versuchen
                </Button>
              ) : null}

              <Button
                as={Link}
                href="/"
                width="responsive"
                classNames={{ root: 'text-white border-white' }}
                variant={onRetry ? 'secondary' : 'primary'}
              >
                Zur Startseite
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
